import React, { createContext, useContext, useState, useEffect } from 'react';

// Define supported languages
export type Language = 'en' | 'fr' | 'ar';

interface LanguageContextProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
  getTextDirection: (forceDirection?: 'ltr' | 'rtl') => "ltr" | "rtl";
  getTextAlign: (forceAlign?: 'left' | 'right') => "left" | "right";
  getFlexDirection: (defaultDirection?: 'row' | 'column') => "row" | "row-reverse" | "column" | "column-reverse";
}

export const LanguageContext = createContext<LanguageContextProps>({
  language: 'en',
  setLanguage: () => {},
  isRTL: false,
  getTextDirection: () => 'ltr',
  getTextAlign: () => 'left',
  getFlexDirection: () => 'row',
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Get saved language preference from localStorage with fallback to browser language
    const savedLanguage = localStorage.getItem('aquagenius-language') as Language;
    if (savedLanguage) return savedLanguage;
    
    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'fr') return 'fr';
    if (browserLang === 'ar') return 'ar';
    return 'en';
  });
  
  const isRTL = language === 'ar';

  useEffect(() => {
    // Save language preference
    localStorage.setItem('aquagenius-language', language);
    
    // Update document direction for RTL support
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Add RTL class to body for global styling
    if (isRTL) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
    
    // Force re-render of RTL-sensitive components
    window.dispatchEvent(new Event('languagechange'));
  }, [language, isRTL]);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  // Helper functions for RTL support
  const getTextDirection = (forceDirection?: 'ltr' | 'rtl'): "ltr" | "rtl" => {
    if (forceDirection) return forceDirection;
    return isRTL ? 'rtl' : 'ltr';
  };

  const getTextAlign = (forceAlign?: 'left' | 'right'): "left" | "right" => {
    if (forceAlign) return forceAlign;
    return isRTL ? 'right' : 'left';
  };

  const getFlexDirection = (defaultDirection?: 'row' | 'column'): "row" | "row-reverse" | "column" | "column-reverse" => {
    const direction = defaultDirection || 'row';
    if (direction === 'row') {
      return isRTL ? 'row-reverse' : 'row';
    }
    return direction;
  };

  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        setLanguage, 
        isRTL, 
        getTextDirection, 
        getTextAlign, 
        getFlexDirection 
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
